import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/pricejob-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | PriceAJob Alternative for UK Builders",
  "description": "Looking for a PriceAJob alternative for UK builders? The Proper Tool offers AI-powered quoting, a 31-category UK cost library, and a 14-day free trial from £29/mo.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "PriceAJob Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best PriceAJob alternative for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK builders who need complete, professional construction quoting rather than element-level cost calculators, The Proper Tool is the strongest PriceAJob alternative. It generates fully itemised scopes from drawings or a plain description using AI, has a 31-category UK cost library built in, and starts at £29/month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "What is PriceAJob and who is it for?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "PriceAJob (pricejob.co.uk) is a UK quoting tool with approximately 17 element-level cost-calculator pages covering areas like foundation, roof, and drainage costs. It is primarily useful for builders who want to quickly estimate the cost of a specific construction element rather than produce a complete scope-of-works quote. It also has an HMRC Making Tax Digital listing and a loyalty programme.",
      },
    },
    {
      "@type": "Question",
      "name": "Can PriceAJob produce a complete construction quote?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "PriceAJob is structured around individual element cost calculators rather than complete construction quotes with full scopes of works. It does not offer AI scope generation, drawing upload, or a professional branded PDF output in the same way as a dedicated quoting platform.",
      },
    },
    {
      "@type": "Question",
      "name": "Does PriceAJob read structural drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. PriceAJob does not read architectural drawings or PDFs. The Proper Tool reads uploaded structural drawing PDFs using AI, extracting beam sizes, floor areas, and dimensions to generate a more accurate and complete construction quote.",
      },
    },
    {
      "@type": "Question",
      "name": "How does PriceAJob pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Price A Job lists Estimating from £35.95/month and All Included at £89.75/month. It offers a free demo with no card, works on every device, and includes drawing/document management, quantity and material takeoff and blueprint import.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  pricejob: string;
  pricejobState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "Complete construction quote generation", pricejob: "Yes: estimating plans", pricejobState: "yes", tpt: "Yes: full scope of works", tptState: "yes" },
  { feature: "AI scope generation from description", pricejob: "No", pricejobState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", pricejob: "Blueprint import/takeoff (partial/manual)", pricejobState: "partial", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "UK-specific construction pricing", pricejob: "5,000+ prices, nightly updates", pricejobState: "yes", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "Scope completeness validation", pricejob: "No", pricejobState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Branded PDF quote output", pricejob: "No", pricejobState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Labour / materials / plant split", pricejob: "No", pricejobState: "no", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Schema markup (SEO trust signals)", pricejob: "None", pricejobState: "no", tpt: "Full structured data", tptState: "yes" },
  { feature: "Client-facing professional quote", pricejob: "No", pricejobState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Free trial available", pricejob: "Free demo, no card", pricejobState: "yes", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Starting price", pricejob: "From £35.95/month", pricejobState: "partial", tpt: "£29/month (per account)", tptState: "yes" },
  { feature: "Credit card required to trial", pricejob: "No card for demo", pricejobState: "yes", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "Takeoff is assisted rather than autonomous",
    desc: "Price A Job supports drawing and document management, blueprint import and quantity/material takeoff. These are useful estimating aids, but there is no evidence of AI reading a scope and producing a priced quote.",
  },
  {
    title: "No evidence of AI scope-to-priced-quote generation",
    desc: "Price A Job provides estimating and takeoff tools, but there is no evidence of AI turning a job scope into a priced quote.",
  },
  {
    title: "Plan selection affects the price",
    desc: "Price A Job lists Estimating from £35.95/month and All Included at £89.75/month. Compare the included takeoff, pricing and document features with your workflow.",
  },
  {
    title: "Takeoff remains partly manual",
    desc: "Blueprint import and quantity/material takeoff can reduce manual work, but should be treated as partial assistance rather than autonomous drawing interpretation.",
  },
];

const STAY_ON_PRICEJOB = [
  "You primarily need quick ballpark estimates for individual construction elements rather than full client-facing quotes.",
  "You use PriceAJob calculators as a reference check alongside your main quoting process.",
  "You are enrolled in the PriceAJob loyalty or affiliate programme and derive value from that relationship.",
];

const SWITCH_REASONS = [
  "You need to produce a complete, professional scope-of-works quote that you can send directly to a client.",
  "You want to generate a quote from drawings or a description without manual calculation.",
  "You need a branded PDF output that presents your business professionally.",
  "You want UK-specific pricing across all 31 residential construction trade categories.",
  "You want scope completeness validation to catch items you may have missed.",
  "You want a 14-day free trial with no credit card and transparent pricing before you commit.",
];

const FAQS = [
  {
    q: "What is the best PriceAJob alternative for UK builders?",
    a: "For UK builders who need complete, professional construction quoting rather than element-level cost calculators, The Proper Tool is the strongest PriceAJob alternative. It generates fully itemised scopes from drawings or a plain description using AI, has a 31-category UK cost library built in, and starts at £29/month with a 14-day free trial.",
  },
  {
    q: "What is PriceAJob and who is it for?",
    a: "PriceAJob (pricejob.co.uk) is a UK quoting tool with approximately 17 element-level cost-calculator pages covering foundation, roof, and drainage costs. It is primarily useful for builders who want to quickly estimate the cost of a specific construction element rather than produce a complete scope-of-works quote.",
  },
  {
    q: "Can PriceAJob produce a complete construction quote?",
    a: "PriceAJob is structured around individual element cost calculators rather than complete construction quotes with full scopes of works. It does not offer AI scope generation, drawing upload, or a professional branded PDF output in the same way as a dedicated quoting platform.",
  },
  {
    q: "Does PriceAJob read structural drawings?",
    a: "No. PriceAJob does not read architectural drawings or PDFs. The Proper Tool reads uploaded structural drawing PDFs using AI, extracting beam sizes, floor areas, and dimensions to generate a more accurate and complete construction quote.",
  },
  {
    q: "How does PriceAJob pricing compare to The Proper Tool?",
    a: "Price A Job lists Estimating from £35.95/month and All Included at £89.75/month. It offers a free demo with no card. The Proper Tool starts at £29 per month with a 14-day free trial.",
  },
];

export default function PricejobAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="PriceAJob Alternative for UK Builders: Complete Construction Quoting | The Proper Tool"
        description="Looking for a PriceAJob alternative? The Proper Tool offers complete scope-of-works quoting with AI, a 31-category UK cost library, and a 14-day free trial from £29/mo."
        canonical="/pricejob-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>PriceAJob Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              PriceAJob Alternative for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              PriceAJob offers element-level cost calculators for specific construction items. If you need a complete, AI-powered scope-of-works quote you can send to a client, here is how The Proper Tool compares.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why builders look for a PriceAJob alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              PriceAJob provides useful element-level cost reference calculators. But builders who need to produce complete professional quotes consistently run into the same limitations:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs PriceAJob: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">PriceAJob</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.pricejob} state={row.pricejobState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
               Competitor pricing and feature information was checked on 19 September 2026. Prices and features may have changed. Verify directly with Price A Job before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Price A Job lists Estimating from £35.95/month and All Included at £89.75/month. It offers a free demo with no card, works on every device, and includes 5,000+ material prices with nightly updates.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">PriceAJob</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Estimating from £35.95/month; All Included £89.75/month</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />Free demo with no card; works on every device</li>
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Blueprint import and quantity/material takeoff are partial/manual</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />5,000+ material prices with nightly updates</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month, all features included</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial, no credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />Complete scope of works, branded PDF output</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />AI-powered, UK-only pricing across 31 trade categories</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on PriceAJob</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              PriceAJob serves a specific use case. If the following describes your situation, it may still be useful:
            </p>
            <div className="space-y-3">
              {STAY_ON_PRICEJOB.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons builders switch from PriceAJob to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">The switch is typically from reference calculators to a complete, client-ready quoting tool.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with PriceAJob before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all quoting tools for UK builders" },
                { href: "/easyestimate-alternative", label: "EasyEstimate Alternative" },
                { href: "/estimage-alternative", label: "esti-mate Alternative" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg border border-border p-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
